import { ResolvedEntity } from './common-types';
import { EntityType } from './entity-type';

/**
 * Account (or similar) badge nested under `shared.shared_from` / `shared.shared_with`
 * on extended API responses.
 */
export interface SharedAccountBadge {
    name: string;
    entity_type: EntityType | string;
    entity_id: string;
    account_id?: string;
    foreign?: boolean;
    data?: { [key: string]: unknown };
}

/** Cross-account sharing block on extended list/detail entities. */
export interface EntitySharing {
    /** Present when this object was shared with the current account (incoming). */
    shared_from?: SharedAccountBadge | null;
    /** Accounts this object is shared with (outgoing). */
    shared_with?: SharedAccountBadge[] | null;
}

export function isSharedIncoming(shared?: EntitySharing | null): boolean {
    return Boolean(shared?.shared_from?.entity_id || shared?.shared_from?.name);
}

export function isSharedOutgoing(shared?: EntitySharing | null): boolean {
    return Boolean(shared?.shared_with && shared.shared_with.length > 0);
}

/** Display name for the account that shared this object with us. */
export function sharedFromAccountName(shared?: EntitySharing | null): string | undefined {
    const name = shared?.shared_from?.name;
    return typeof name === 'string' && name.length > 0 ? name : undefined;
}

/** Account id for the account that shared this object with us. */
export function sharedFromAccountId(shared?: EntitySharing | null): string | undefined {
    const from = shared?.shared_from;
    if (!from) {
        return undefined;
    }
    if (typeof from.account_id === 'string' && from.account_id.length > 0) {
        return from.account_id;
    }
    if (from.entity_type === EntityType.ACCOUNT || from.entity_type === 'account') {
        return from.entity_id;
    }
    return undefined;
}

/**
 * Prefer an explicit account name, then parent `shared.shared_from.name`,
 * for foreign nested ResolvedEntity badges.
 */
export function foreignBadgeAccountName(
    entity: Pick<ResolvedEntity, 'foreign'> & { shared?: EntitySharing | null },
    parentShared?: EntitySharing | null,
    explicitName?: string | null
): string | undefined {
    if (explicitName) {
        return explicitName;
    }
    if (entity.shared) {
        return sharedFromAccountName(entity.shared);
    }
    return sharedFromAccountName(parentShared);
}

/** True when the row has incoming and/or outgoing share metadata. */
export function isShared(shared?: EntitySharing | null): boolean {
    return isSharedIncoming(shared) || isSharedOutgoing(shared);
}

/** Prefer shared_from name, else first shared_with name — for primary-cell share markers. */
export function sharedAccountDisplayName(shared?: EntitySharing | null): string | undefined {
    const fromName = sharedFromAccountName(shared);
    if (fromName) {
        return fromName;
    }
    const withName = shared?.shared_with?.[0]?.name;
    return typeof withName === 'string' && withName.length > 0 ? withName : undefined;
}

/** Prefer shared_from account id, else first shared_with account id. */
export function sharedAccountDisplayId(shared?: EntitySharing | null): string | undefined {
    const fromId = sharedFromAccountId(shared);
    if (fromId) {
        return fromId;
    }
    const target = shared?.shared_with?.[0];
    if (!target) {
        return undefined;
    }
    if (typeof target.account_id === 'string' && target.account_id.length > 0) {
        return target.account_id;
    }
    if (target.entity_type === EntityType.ACCOUNT || target.entity_type === 'account') {
        return target.entity_id;
    }
    return undefined;
}
